/**
 * CongesDashboardSkeleton Component
 * Full skeleton loader for the Conges Dashboard (simplified layout)
 * Matches the layout of CongesDashboardView (metrics row + requests table)
 */

import { SkeletonBase } from "@components/common/skeletons";
import type React from "react";
import { cn } from "@/lib/utils/cn";

interface CongesDashboardSkeletonProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Balance card skeleton for leave balance cards
 */
function BalanceCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        "card bg-base-200 border border-base-300/60 rounded-xl overflow-hidden",
        className,
      )}
      style={style}
    >
      <div className="card-body p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <SkeletonBase variant="line" className="h-4 w-28 mb-2" />
            <SkeletonBase variant="line" className="h-3 w-16" />
          </div>
          <SkeletonBase variant="line" className="h-6 w-6 rounded-lg" />
        </div>

        {/* Value */}
        <div className="mb-4">
          <SkeletonBase variant="line" className="h-9 w-16 mb-2" />
          <SkeletonBase variant="line" className="h-4 w-24" />
        </div>

        {/* Progress bar */}
        <div className="space-y-2">
          <SkeletonBase variant="line" className="h-2.5 w-full rounded-full" />
          <div className="flex justify-between">
            <SkeletonBase variant="line" className="h-3 w-16" />
            <SkeletonBase variant="line" className="h-3 w-12" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Requests table skeleton
 */
function RequestsTableSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        "card bg-base-200 border border-base-300/60 rounded-xl overflow-hidden",
        className,
      )}
      style={style}
    >
      <div className="card-body p-0">
        {/* Header */}
        <div className="p-6 border-b border-base-200/60 bg-base-100/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <SkeletonBase variant="line" className="h-10 w-10 rounded-xl" />
              <div className="space-y-2">
                <SkeletonBase variant="line" className="h-5 w-32" />
                <SkeletonBase variant="line" className="h-3 w-20" />
              </div>
            </div>
            <SkeletonBase variant="line" className="h-10 w-36 rounded-lg" />
          </div>
        </div>

        {/* Table placeholder */}
        <div className="p-6">
          <div className="space-y-3">
            {/* Table header */}
            <div className="grid grid-cols-4 gap-4 pb-3 border-b border-base-200/60">
              <SkeletonBase variant="line" className="h-4 w-16" />
              <SkeletonBase variant="line" className="h-4 w-24" />
              <SkeletonBase variant="line" className="h-4 w-16" />
              <SkeletonBase variant="line" className="h-4 w-20" />
            </div>
            {/* Table rows */}
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="grid grid-cols-4 gap-4 py-3">
                <SkeletonBase variant="line" className="h-4 w-20" />
                <SkeletonBase variant="line" className="h-4 w-40" />
                <SkeletonBase variant="line" className="h-4 w-16" />
                <SkeletonBase variant="line" className="h-6 w-20 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Full conges dashboard skeleton
 * Matches the simplified layout of CongesDashboardView
 */
export function CongesDashboardSkeleton({ className }: CongesDashboardSkeletonProps) {
  return (
    <div className={cn("space-y-6 animate-pulse", className)}>
      {/* Balance cards row - 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
        <BalanceCardSkeleton className="animate-slide-in-left" style={{ animationDelay: "0ms" }} />
        <BalanceCardSkeleton className="animate-slide-in-left" style={{ animationDelay: "50ms" }} />
        <BalanceCardSkeleton
          className="animate-slide-in-left"
          style={{ animationDelay: "100ms" }}
        />
        <BalanceCardSkeleton
          className="animate-slide-in-left"
          style={{ animationDelay: "150ms" }}
        />
      </div>

      {/* Requests table */}
      <RequestsTableSkeleton className="animate-fade-in" style={{ animationDelay: "200ms" }} />
    </div>
  );
}

/**
 * Compact skeleton for inline use
 */
export function CongesDashboardSkeletonCompact({ className }: CongesDashboardSkeletonProps) {
  return (
    <div className={cn("space-y-4 animate-pulse", className)}>
      {/* Balance cards */}
      <div className="grid grid-cols-3 gap-3">
        <BalanceCardSkeleton />
        <BalanceCardSkeleton />
        <BalanceCardSkeleton />
      </div>

      {/* Table */}
      <RequestsTableSkeleton />
    </div>
  );
}

/**
 * Single widget skeleton
 */
export function CongesWidgetSkeleton({
  variant = "balance",
  className,
}: {
  variant?: "balance" | "table";
  className?: string;
}) {
  switch (variant) {
    case "balance":
      return <BalanceCardSkeleton className={className} />;
    case "table":
      return <RequestsTableSkeleton className={className} />;
    default:
      return <BalanceCardSkeleton className={className} />;
  }
}
